import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMapPin, FiCalendar, FiUser, FiMail, FiPhone, FiPackage, FiUsers, FiChevronDown, FiMessageSquare } from 'react-icons/fi';
import { GoogleMap, DirectionsRenderer, Marker } from '@react-google-maps/api';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useMaps } from './MapsProvider';
import PickupMapModal from './PickupMapModal';
import DropoffMapModal from './DropoffMapModal';
import CustomDateTimePicker from './CustomDateTimePicker';

import { formatGreeceLocalTime, getGreeceTimezoneInfo } from '../utils/timezone';
import { validateBookingForm, checkRateLimit } from '../utils/validation';

// Default Lasmari location
const lasmariCoords = {
  lat: 39.796276,
  lng: 19.884997
};

// Helper function to get API base URL
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isLocalNetwork = hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.');
  const isDevelopment = isLocalhost || isLocalNetwork || hostname.includes('.local');
  
  if (isDevelopment) {
    return `${protocol}//${hostname}:5050`;
  } else {
    return process.env.REACT_APP_API_URL || `${protocol}//${hostname}:5050`;
  }
};

const BookingForm = ({ 
  onBookingSuccess, 
  onBookingError, 
  setShowUnavailableModal,
  setUnavailableDateTime,
  setShowLargeGroupModal,
  setLargeGroupCount 
}) => {
  // State variables
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isDropoffModalOpen, setIsDropoffModalOpen] = useState(false);
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [price, setPrice] = useState(null);
  const [vansNeeded, setVansNeeded] = useState(1);
  const [basePrice, setBasePrice] = useState(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+30");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const countryDropdownRef = useRef(null);
  const [passengers, setPassengers] = useState("");
  const [luggage, setLuggage] = useState("");
  const [comments, setComments] = useState("");
  const [datetime, setDatetime] = useState(null);
  const [isAirportPickup, setIsAirportPickup] = useState(false);
  const [flightNumber, setFlightNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [directions, setDirections] = useState(null);
  const [isLoadingDirections, setIsLoadingDirections] = useState(false);
  const { isLoaded, createMarkerIcon, mapOptions } = useMaps();
  const directionsServiceRef = useRef(null);

  const [validationErrors, setValidationErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Country codes with flags, Greece first, then most-common European callers
  const countryCodes = [
    { code: "+30",  country: "Greece",                  flag: "🇬🇷" },
    { code: "+44",  country: "United Kingdom",          flag: "🇬🇧" },
    { code: "+1",   country: "United States",           flag: "🇺🇸" },
    { code: "+1",   country: "Canada",                  flag: "🇨🇦" },
    { code: "+49",  country: "Germany",                 flag: "🇩🇪" },
    { code: "+33",  country: "France",                  flag: "🇫🇷" },
    { code: "+39",  country: "Italy",                   flag: "🇮🇹" },
    { code: "+34",  country: "Spain",                   flag: "🇪🇸" },
    { code: "+31",  country: "Netherlands",             flag: "🇳🇱" },
    { code: "+32",  country: "Belgium",                 flag: "🇧🇪" },
    { code: "+43",  country: "Austria",                 flag: "🇦🇹" },
    { code: "+41",  country: "Switzerland",             flag: "🇨🇭" },
    { code: "+351", country: "Portugal",                flag: "🇵🇹" },
    { code: "+353", country: "Ireland",                 flag: "🇮🇪" },
    { code: "+45",  country: "Denmark",                 flag: "🇩🇰" },
    { code: "+46",  country: "Sweden",                  flag: "🇸🇪" },
    { code: "+47",  country: "Norway",                  flag: "🇳🇴" },
    { code: "+358", country: "Finland",                 flag: "🇫🇮" },
    { code: "+354", country: "Iceland",                 flag: "🇮🇸" },
    { code: "+48",  country: "Poland",                  flag: "🇵🇱" },
    { code: "+420", country: "Czech Republic",          flag: "🇨🇿" },
    { code: "+421", country: "Slovakia",                flag: "🇸🇰" },
    { code: "+36",  country: "Hungary",                 flag: "🇭🇺" },
    { code: "+40",  country: "Romania",                 flag: "🇷🇴" },
    { code: "+359", country: "Bulgaria",                flag: "🇧🇬" },
    { code: "+386", country: "Slovenia",                flag: "🇸🇮" },
    { code: "+385", country: "Croatia",                 flag: "🇭🇷" },
    { code: "+387", country: "Bosnia & Herzegovina",    flag: "🇧🇦" },
    { code: "+381", country: "Serbia",                  flag: "🇷🇸" },
    { code: "+382", country: "Montenegro",              flag: "🇲🇪" },
    { code: "+389", country: "North Macedonia",         flag: "🇲🇰" },
    { code: "+355", country: "Albania",                 flag: "🇦🇱" },
    { code: "+357", country: "Cyprus",                  flag: "🇨🇾" },
    { code: "+356", country: "Malta",                   flag: "🇲🇹" },
    { code: "+372", country: "Estonia",                 flag: "🇪🇪" },
    { code: "+371", country: "Latvia",                  flag: "🇱🇻" },
    { code: "+370", country: "Lithuania",               flag: "🇱🇹" },
    { code: "+352", country: "Luxembourg",              flag: "🇱🇺" },
    { code: "+7",   country: "Russia",                  flag: "🇷🇺" },
    { code: "+380", country: "Ukraine",                 flag: "🇺🇦" },
    { code: "+375", country: "Belarus",                 flag: "🇧🇾" },
    { code: "+90",  country: "Turkey",                  flag: "🇹🇷" },
    { code: "+972", country: "Israel",                  flag: "🇮🇱" },
    { code: "+971", country: "United Arab Emirates",    flag: "🇦🇪" },
    { code: "+966", country: "Saudi Arabia",            flag: "🇸🇦" },
    { code: "+974", country: "Qatar",                   flag: "🇶🇦" },
    { code: "+973", country: "Bahrain",                 flag: "🇧🇭" },
    { code: "+968", country: "Oman",                    flag: "🇴🇲" },
    { code: "+965", country: "Kuwait",                  flag: "🇰🇼" },
    { code: "+962", country: "Jordan",                  flag: "🇯🇴" },
    { code: "+961", country: "Lebanon",                 flag: "🇱🇧" },
    { code: "+20",  country: "Egypt",                   flag: "🇪🇬" },
    { code: "+212", country: "Morocco",                 flag: "🇲🇦" },
    { code: "+216", country: "Tunisia",                 flag: "🇹🇳" },
    { code: "+27",  country: "South Africa",            flag: "🇿🇦" },
    { code: "+234", country: "Nigeria",                 flag: "🇳🇬" },
    { code: "+254", country: "Kenya",                   flag: "🇰🇪" },
    { code: "+86",  country: "China",                   flag: "🇨🇳" },
    { code: "+81",  country: "Japan",                   flag: "🇯🇵" },
    { code: "+82",  country: "South Korea",             flag: "🇰🇷" },
    { code: "+91",  country: "India",                   flag: "🇮🇳" },
    { code: "+65",  country: "Singapore",               flag: "🇸🇬" },
    { code: "+60",  country: "Malaysia",                flag: "🇲🇾" },
    { code: "+66",  country: "Thailand",                flag: "🇹🇭" },
    { code: "+62",  country: "Indonesia",               flag: "🇮🇩" },
    { code: "+63",  country: "Philippines",             flag: "🇵🇭" },
    { code: "+84",  country: "Vietnam",                 flag: "🇻🇳" },
    { code: "+852", country: "Hong Kong",               flag: "🇭🇰" },
    { code: "+886", country: "Taiwan",                  flag: "🇹🇼" },
    { code: "+61",  country: "Australia",               flag: "🇦🇺" },
    { code: "+64",  country: "New Zealand",             flag: "🇳🇿" },
    { code: "+52",  country: "Mexico",                  flag: "🇲🇽" },
    { code: "+55",  country: "Brazil",                  flag: "🇧🇷" },
    { code: "+54",  country: "Argentina",               flag: "🇦🇷" },
    { code: "+56",  country: "Chile",                   flag: "🇨🇱" },
    { code: "+57",  country: "Colombia",                flag: "🇨🇴" },
    { code: "+51",  country: "Peru",                    flag: "🇵🇪" }
  ];

  // Initialize directions service
  useEffect(() => {
    if (isLoaded && window.google && !directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }
  }, [isLoaded]);

  // Listen for destination events
  useEffect(() => {
    const handleSetDestination = (event) => {
      const destination = event.detail;
      setDropoff({
        name: destination.name,
        position: destination.position,
        lat: destination.position.lat,
        lng: destination.position.lng
      });
    };
    window.addEventListener('setDestination', handleSetDestination);
    return () => window.removeEventListener('setDestination', handleSetDestination);
  }, []);

  // Pre-fill pickup/dropoff from URL search params (e.g. /booking?pickup_name=...&pickup_lat=...&dropoff_name=...&dropoff_lat=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pickupName = params.get('pickup_name');
    const pickupLat = parseFloat(params.get('pickup_lat'));
    const pickupLng = parseFloat(params.get('pickup_lng'));
    const dropoffName = params.get('dropoff_name');
    const dropoffLat = parseFloat(params.get('dropoff_lat'));
    const dropoffLng = parseFloat(params.get('dropoff_lng'));

    if (pickupName && !isNaN(pickupLat) && !isNaN(pickupLng)) {
      setPickup({
        name: pickupName,
        position: { lat: pickupLat, lng: pickupLng },
        lat: pickupLat,
        lng: pickupLng
      });
    }
    if (dropoffName && !isNaN(dropoffLat) && !isNaN(dropoffLng)) {
      setDropoff({
        name: dropoffName,
        position: { lat: dropoffLat, lng: dropoffLng },
        lat: dropoffLat,
        lng: dropoffLng
      });
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter countries
  const filteredCountries = countryCodes.filter(item => 
    item.country.toLowerCase().includes(countrySearch.toLowerCase()) || 
    item.code.includes(countrySearch)
  );

  // Price calculation
  const calculatePrice = useCallback((pickup, dropoff, passengers, luggage) => {
    if (!pickup || !dropoff) return;
    
    setIsCalculating(true);
    setErrorMessage("");
    
    const apiUrl = `${getApiBaseUrl()}/api/price`;
    
    axios.post(apiUrl, {
      pickup: { lat: pickup.position.lat, lng: pickup.position.lng },
      dropoff: { lat: dropoff.position.lat, lng: dropoff.position.lng },
      passengers: Number(passengers) || 1,
      luggage: Number(luggage) || 0
    })
    .then(response => {
      const { distance, duration, price, base_price, vans_needed } = response.data;
      setDistance(distance);
      setDuration(duration);
      setPrice(price);
      setBasePrice(base_price);
      setVansNeeded(vans_needed || 1);
    })
    .catch(error => {
      setErrorMessage("Price calculation failed. Please try again.");
    })
    .finally(() => {
      setIsCalculating(false);
    });
  }, []);

  // Calculate route and price
  useEffect(() => {
    if (!pickup || !dropoff || !isLoaded) return;
    
    // Calculate route
    if (directionsServiceRef.current) {
      setIsLoadingDirections(true);
      directionsServiceRef.current.route({
        origin: pickup.position,
        destination: dropoff.position,
        travelMode: window.google.maps.TravelMode.DRIVING
      })
      .then(result => setDirections(result))
      .catch(error => console.log("Route visualization failed"))
      .finally(() => setIsLoadingDirections(false));
    }
    
    // Calculate price
    calculatePrice(pickup, dropoff, passengers, luggage);
  }, [pickup, dropoff, passengers, luggage, isLoaded, calculatePrice]);

  const handlePassengersChange = (e) => {
    const newValue = e.target.value;
    const passengerCount = parseInt(newValue) || 0;
    
    if (passengerCount > 21) {
      setLargeGroupCount(passengerCount);
      setShowLargeGroupModal(true);
      return;
    }
    setPassengers(newValue);
    
    // Auto-adjust luggage if it exceeds the new limit
    const vansNeeded = Math.ceil(passengerCount / 7) || 1;
    const maxLuggage = vansNeeded * 8;
    const currentLuggage = parseInt(luggage) || 0;
    
    if (currentLuggage > maxLuggage) {
      setLuggage(maxLuggage.toString());
    }
  };

  const handleLuggageChange = (e) => {
    const newValue = e.target.value;
    const luggageCount = parseInt(newValue) || 0;
    const passengerCount = parseInt(passengers) || 1;
    const vansNeeded = Math.ceil(passengerCount / 7);
    const maxLuggage = vansNeeded * 8;
    
    if (luggageCount > maxLuggage) {
      setLuggage(maxLuggage.toString());
    } else {
      setLuggage(newValue);
    }
  };

  const handleNewBooking = () => {
    setPickup(null);
    setDropoff(null);
    setFullName("");
    setEmail("");
    setPhone("");
    setPassengers("");
    setLuggage("");
    setComments("");
    setDatetime(null);
    setPrice(null);
    setDistance(null);
    setDuration(null);
    setErrorMessage("");
    setSuccessMessage("");
    setShowSummary(false);
    setValidationErrors([]);
    
    setDirections(null);
  };

  const handleConfirmBooking = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setValidationErrors([]);
    setIsSubmitting(true);

    try {
      const rateLimitCheck = checkRateLimit('booking_submission', 3, 300000);
      if (!rateLimitCheck.allowed) {
        toast.error(rateLimitCheck.message);
        setIsSubmitting(false);
        return;
      }

      

      const formData = {
        fullName, email, phone, countryCode,
        passengers: parseInt(passengers) || 1,
        luggage: parseInt(luggage) || 0,
        datetime, pickup, dropoff
      };

      const validation = validateBookingForm(formData);

      const extraErrors = [];
      if (isAirportPickup && !flightNumber.trim()) {
        extraErrors.push('Flight number is required for airport pickups');
      }

      if (!validation.isValid || extraErrors.length > 0) {
        setValidationErrors([...validation.errors, ...extraErrors]);
        toast.error("⚠️ Please correct the errors below.");
        setIsSubmitting(false);
        return;
      }

      const sanitizedData = validation.sanitizedData;
      const localDatetimeString = formatGreeceLocalTime(sanitizedData.datetime);

      const bookingDetails = {
        fullName: sanitizedData.fullName,
        email: sanitizedData.email,
        phone: sanitizedData.phone,
        passengers: sanitizedData.passengers,
        luggage: sanitizedData.luggage,
        pickup: sanitizedData.pickup,
        dropoff: sanitizedData.dropoff,
        datetime: localDatetimeString,
        price: parseFloat(price),
        base_price: parseFloat(basePrice),
        distance, duration, vans_needed: vansNeeded,
        comments: comments.trim(),
        is_airport_pickup: isAirportPickup,
        flight_number: isAirportPickup ? flightNumber.trim() : null,
        auto_assign: true
      };

      const res = await axios.post(`${getApiBaseUrl()}/api/bookings`, bookingDetails);

      if (res.data.success) {
        toast.success("✅ Booking confirmed! We will contact you via WhatsApp.");
        setShowSummary(true);
        handleNewBooking();
        if (onBookingSuccess) onBookingSuccess(bookingDetails);
      } else {
        throw new Error(res.data.message || 'Booking failed');
      }
    } catch (error) {
      let errorMsg = "Booking failed. Please try again.";
      
      if (error.response?.status === 409) {
        const conflictData = error.response.data;
        if (conflictData.code === 'BOOKING_TIME_UNAVAILABLE') {
          setUnavailableDateTime(conflictData.requested_datetime);
          setShowUnavailableModal(true);
          errorMsg = "This time slot is no longer available.";
        }
      }
      
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      if (onBookingError) onBookingError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="relative py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-black/10 outline-none"
        >
          <h2 className="text-4xl font-light mb-8 text-center text-[#1a1012] tracking-wide">
            Reserve Your Transfer
          </h2>

          <AnimatePresence>
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mb-6 p-4 bg-[#fbeceb] border border-[#ba1609]/30 rounded-xl text-center text-[#ba1609]"
              >
                {errorMessage}
              </motion.div>
            )}
          </AnimatePresence>

          {showSummary ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <button
                onClick={handleNewBooking}
                className="mt-6 px-8 py-4 bg-[#ba1609] text-white rounded-full font-bold hover:bg-[#8e1107] transition-all duration-300 shadow-lg outline-none focus:outline-none focus:ring-2 focus:ring-[#ba1609] focus:ring-offset-2 focus:ring-offset-white"
              >
                Reserve Another Journey
              </button>
            </motion.div>
          ) : (
            <div className="space-y-8">
              {/* Location Selection */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                    <FiMapPin className="mr-2 text-[#ba1609]" />
                    Pickup Location
                  </label>
                  <button
                    onClick={() => setIsPickupModalOpen(true)}
                    className="w-full p-4 bg-white border border-black/10 rounded-xl text-left text-[#1a1012] hover:bg-[#faf7f6] transition-colors duration-300 flex items-center outline-none focus:outline-none focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0"
                  >
                    <span className="flex-1">{pickup?.name || "Select pickup location"}</span>
                    <FiMapPin className="text-[#ba1609]" />
                  </button>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                    <FiMapPin className="mr-2 text-[#ba1609]" />
                    Drop-off Location
                  </label>
                  <button
                    onClick={() => setIsDropoffModalOpen(true)}
                    className="w-full p-4 bg-white border border-black/10 rounded-xl text-left text-[#1a1012] hover:bg-[#faf7f6] transition-colors duration-300 flex items-center outline-none focus:outline-none focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0"
                  >
                    <span className="flex-1">{dropoff?.name || "Select drop-off location"}</span>
                    <FiMapPin className="text-[#ba1609]" />
                  </button>
                </div>
              </div>

              {/* Route Map */}
              {pickup && dropoff && isLoaded && (
                <div className="h-[450px] overflow-hidden rounded-xl relative">
                  {isLoadingDirections && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
                      <div className="animate-spin h-12 w-12 border-4 border-[#ba1609] rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={pickup?.position || lasmariCoords}
                    zoom={8.5}
                    options={{ ...mapOptions, minZoom: 6, maxZoom: 18 }}
                  >
                    {directions && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{
                          suppressMarkers: true,
                          polylineOptions: {
                            strokeColor: "#ba1609",
                            strokeWeight: 5,
                            strokeOpacity: 0.7
                          }
                        }}
                      />
                    )}
                    
                    {pickup && (
                      <Marker 
                        position={pickup.position} 
                        icon={createMarkerIcon('pickup')}
                      />
                    )}
                    
                    {dropoff && (
                      <Marker 
                        position={dropoff.position} 
                        icon={createMarkerIcon('dropoff')}
                      />
                    )}
                  </GoogleMap>
                </div>
              )}

              {/* Contact Information */}
              <div className="space-y-6">
                <div>
                  <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                    <FiUser className="mr-2 text-[#ba1609]" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full p-4 bg-white border border-black/10 rounded-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                      <FiMail className="mr-2 text-[#ba1609]" />
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="e.g. john@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-4 bg-white border border-black/10 rounded-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                      <FiPhone className="mr-2 text-[#ba1609]" />
                      Phone Number
                    </label>
                    <div className="relative flex">
                      <button
                        type="button"
                        className="flex items-center justify-between p-4 bg-white border border-black/10 rounded-l-xl focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none text-[#1a1012] w-[120px]"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-lg leading-none">
                            {countryCodes.find((c) => c.code === countryCode)?.flag || '🌐'}
                          </span>
                          <span>{countryCode}</span>
                        </span>
                        <FiChevronDown className={`ml-2 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {showCountryDropdown && (
                        <div
                          ref={countryDropdownRef}
                          className="absolute top-full left-0 z-10 mt-1 bg-white border border-black/10 rounded-lg shadow-xl w-[290px] max-h-[360px] overflow-y-auto"
                        >
                          <div className="sticky top-0 bg-white border-b border-black/10 p-2">
                            <input
                              type="search"
                              autoFocus
                              placeholder="Search country…"
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              className="w-full px-3 py-2 bg-white border border-black/10 rounded text-sm text-[#1a1012] placeholder:text-[#5b4d4f] focus:outline-none focus:border-[#ba1609]"
                            />
                          </div>
                          {filteredCountries.length === 0 ? (
                            <p className="p-4 text-sm text-[#5b4d4f] text-center">No countries match.</p>
                          ) : filteredCountries.map((item) => (
                            <button
                              key={`${item.code}-${item.country}`}
                              type="button"
                              className="w-full p-3 text-left text-[#1a1012] hover:bg-[#fbeceb] flex items-center justify-between gap-3 transition"
                              onClick={() => {
                                setCountryCode(item.code);
                                setShowCountryDropdown(false);
                                setCountrySearch("");
                              }}
                            >
                              <span className="flex items-center gap-2.5 min-w-0">
                                <span className="text-lg leading-none">{item.flag}</span>
                                <span className="text-sm truncate">{item.country}</span>
                              </span>
                              <span className="text-[#5b4d4f] text-sm shrink-0">{item.code}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      <input
                        type="tel"
                        placeholder="e.g. 1234567890"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="flex-1 p-4 bg-white border border-black/10 rounded-r-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                      <FiUsers className="mr-2 text-[#ba1609]" />
                      Number of Passengers
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="21"
                      placeholder="e.g. 2"
                      value={passengers}
                      onChange={handlePassengersChange}
                      className="w-full p-4 bg-white border border-black/10 rounded-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                      <FiPackage className="mr-2 text-[#ba1609]" />
                      Luggage Pieces
                      {(() => {
                        const passengerCount = parseInt(passengers) || 1;
                        const vansNeeded = Math.ceil(passengerCount / 7);
                        const maxLuggage = vansNeeded * 8;
                        return (
                          <span className="ml-2 text-xs text-[#5b4d4f]">
                            (max {maxLuggage} for {vansNeeded} van{vansNeeded > 1 ? 's' : ''})
                          </span>
                        );
                      })()}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={Math.ceil((parseInt(passengers) || 1) / 7) * 8}
                      placeholder="e.g. 3"
                      value={luggage}
                      onChange={handleLuggageChange}
                      className="w-full p-4 bg-white border border-black/10 rounded-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                    />
                  </div>
                </div>

                {/* Airport pickup + required flight number */}
                <div className="bg-[#faf7f6] border border-black/10 rounded-xl p-4">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isAirportPickup}
                      onChange={(e) => setIsAirportPickup(e.target.checked)}
                      className="w-4 h-4 cursor-pointer accent-[#ba1609]"
                    />
                    <span className="text-sm font-medium text-[#1a1012]">This is an airport pickup</span>
                  </label>
                  {isAirportPickup && (
                    <div className="mt-4">
                      <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                        Flight Number <span className="text-[#ba1609] ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        value={flightNumber}
                        onChange={(e) => setFlightNumber(e.target.value)}
                        placeholder="e.g. EJU8501, FR1234, A3712"
                        autoComplete="off"
                        className="w-full p-4 bg-white border border-black/10 rounded-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                      />
                      <p className="text-xs text-[#5b4d4f] mt-1.5">We track your flight and adjust the pickup time for delays.</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                    <FiCalendar className="mr-2 text-[#ba1609]" />
                    Date & Time
                  </label>
                  <CustomDateTimePicker
                    value={datetime}
                    onChange={setDatetime}
                    className="w-full p-4 bg-white border border-black/10 rounded-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                    placeholder="Select date and time"
                  />
                </div>

                {distance && duration && price && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#faf7f6] border border-black/10 rounded-xl p-6"
                  >
                    <h3 className="text-xl font-medium text-[#1a1012] mb-4">Trip Summary</h3>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-[#5b4d4f] font-medium">Distance</div>
                        <div className="text-[#1a1012]">{distance}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[#5b4d4f] font-medium">Duration</div>
                        <div className="text-[#1a1012]">{duration}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[#5b4d4f] font-medium">Total Price</div>
                        <div className="text-[#ba1609] text-xl font-bold">€{price}</div>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div>
                  <label className="flex items-center text-sm font-medium mb-2 text-[#5b4d4f]">
                    <FiMessageSquare className="mr-2 text-[#ba1609]" />
                    Special Requests (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Any special requests or comments..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="w-full p-4 bg-white border border-black/10 rounded-xl text-[#1a1012] placeholder:text-[#5b4d4f] focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                  />
                </div>

                {validationErrors.length > 0 && (
                  <div className="bg-[#fbeceb] border border-[#ba1609]/30 rounded-xl p-4">
                    <h4 className="text-[#ba1609] font-medium mb-2">Please correct the following:</h4>
                    <ul className="text-[#8e1107] text-sm space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleConfirmBooking}
                                      disabled={isSubmitting}
                  className={`mt-6 px-10 py-4 bg-[#ba1609] text-white rounded-full font-medium tracking-wide hover:bg-[#8e1107] transition-all duration-300 shadow-xl w-full md:w-auto outline-none focus:outline-none focus:ring-2 focus:ring-[#ba1609] focus:ring-offset-2 focus:ring-offset-white uppercase text-sm ${
                                          isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 inline-block"></div>
                      Processing...
                    </>
                  ) : (
                    'Confirm Reservation'
                  )}
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <PickupMapModal
        isOpen={isPickupModalOpen}
        onClose={() => setIsPickupModalOpen(false)}
        onConfirm={(location) => {
          setPickup({
            name: location.name || "Pickup Location",
            position: location.position,
            lat: location.position.lat,
            lng: location.position.lng
          });
          setIsPickupModalOpen(false);
        }}
        initialLocation={pickup}
      />

      <DropoffMapModal
        isOpen={isDropoffModalOpen}
        onClose={() => setIsDropoffModalOpen(false)}
        onConfirm={(location) => {
          setDropoff({
            name: location.name || "Dropoff Location",
            position: location.position,
            lat: location.position.lat,
            lng: location.position.lng
          });
          setIsDropoffModalOpen(false);
        }}
        initialLocation={dropoff}
      />
    </section>
  );
};

export default BookingForm; 